/**
 * Variantes del botón usando class-variance-authority
 */

import { cva } from 'class-variance-authority';
import { getVariantColors } from './variantColors';

/**
 * Constantes para el posicionamiento del badge
 */
export const BADGE_POSITION_STYLE: React.CSSProperties = {
  right: 0,
  top: 0,
  transform: 'translate(50%, -50%)',
};

export const BADGE_POSITION_CLASSES = 'ui-absolute ui-z-10 ui-pointer-events-none';

/**
 * Crea las variantes del botón usando CVA
 *
 * @param theme - Tema del botón (solid, icon)
 * @param clickable - Si el botón es clickeable
 * @param borderable - Si el botón debe tener borde
 * @param whiteBackground - Si el botón tiene fondo blanco
 * @param active - Si el botón está en estado activo
 * @param square - Si el botón es cuadrado
 * @param icon - Si el botón tiene icono
 * @param children - Si el botón tiene children
 * @param shouldShowBadge - Si el badge debe mostrarse
 * @param showShadow - Si el botón debe mostrar sombra
 * @param pointer - Si el botón debe mostrar cursor pointer
 * @param transition - Si el botón debe tener transición
 * @param classNameId - ID de clase del botón
 * @returns Función CVA para generar las clases del botón
 */
export const createButtonVariants = (
  theme: string,
  clickable: boolean,
  borderable: boolean | undefined,
  whiteBackground: boolean,
  active: boolean,
  square: boolean,
  icon: boolean,
  children: boolean,
  shouldShowBadge: boolean,
  showShadow: boolean,
  pointer: boolean,
  transition: boolean,
  classNameId: string
) => {
  const squareClasses = square ? '!ui-p-0 ui-flex ui-justify-center ui-items-center' : '';
  const isIconButton = !children && !!icon;

  return cva(
    `ui-imj ${classNameId} ${children ? '' : ''} ui-relative ui-justify-center ui-flex ui-items-center ui-box-border ui-border-solid ui-gap-x-4 ui-border-[1px] ui-border-default ui-cursor-default ${showShadow && theme !== 'icon' ? 'ui-shadow-button' : ''} ${shouldShowBadge ? 'ui-overflow-visible' : 'ui-overflow-hidden'} ${pointer ? '!ui-cursor-pointer disabled:ui-pointer-events-none' : ''} ${transition ? 'ui-transition-all ui-duration-300' : ''} ui-group`,
    {
      variants: {
        theme: {
          solid: '',
          icon: ' ui-border-transparent',
          emoji: ' ui-border-transparent !ui-p-0',
        },
        size: {
          xxs: `ui-max-h-control-xxs ui-min-h-control-xxs ui-min-w-control-xxs ui-font-body-xs-bold ui-leading-body-xs-bold ui-tracking-body-xs-bold ${children ? 'ui-px-8 ui-py-4' : 'ui-p-4 ui-w-control-xxs ui-flex ui-justify-center ui-items-center'} ui-h-control-xxs ui-text-body-xs-bold ${square ? ` ui-max-w-control-xxs ui-min-h-control-xxs ${squareClasses}` : ''}`,
          xs: `ui-max-h-control-xs ui-min-h-control-xs ui-min-w-control-xs ui-font-body-sm-bold ui-leading-body-sm-bold ui-tracking-body-sm ${children ? 'ui-px-12 ui-py-4' : 'ui-p-4 ui-w-control-xs ui-flex ui-justify-center ui-items-center'} ui-h-control-xs ui-text-body-sm-bold ${square ? ` ui-max-w-control-xs ui-min-h-control-xs ${squareClasses}` : ''}`,
          sm: `ui-max-h-control-sm ui-min-h-control-sm ui-min-w-control-sm ui-font-body-sm-bold ui-leading-body-sm-bold ui-tracking-body-sm ${children ? 'ui-px-12 ui-py-6' : 'ui-p-6 ui-w-control-sm ui-flex ui-justify-center ui-items-center'} ui-h-control-sm ui-text-body-sm-bold ${square ? ` ui-max-w-control-sm ui-min-h-control-sm ${squareClasses}` : ''}`,
          md: `ui-max-h-control-md ui-min-h-control-md ui-min-w-control-md ui-font-body-sm-bold ui-leading-body-sm-bold ui-tracking-body-sm ${children ? 'ui-px-16 ui-py-8' : 'ui-p-8 ui-w-control-md ui-flex ui-justify-center ui-items-center'} ui-h-control-md ui-text-body-sm-bold ${square ? ` ui-max-w-control-md ui-min-h-control-md ${squareClasses}` : ''}`,
          lg: `ui-max-h-control-lg ui-min-h-control-lg ui-min-w-control-lg ui-font-body-sm-bold ui-leading-body-sm-bold ui-tracking-body-sm ${children ? 'ui-px-24 ui-py-12' : 'ui-p-12 ui-w-control-lg ui-flex ui-justify-center ui-items-center'} ui-h-control-lg ui-text-body-sm-bold ${square ? ` ui-max-w-control-lg ui-min-h-control-lg ${squareClasses}` : ''}`,
        },
        color: {
          primary: getVariantColors(theme, clickable, borderable, whiteBackground, active).primary,
          secondary: getVariantColors(theme, clickable, borderable, whiteBackground, active)
            .secondary,
          tertiary: getVariantColors(theme, clickable, borderable, whiteBackground, active)
            .tertiary,
          destructive: getVariantColors(theme, clickable, borderable, whiteBackground, active)
            .destructive,
          link: getVariantColors(theme, clickable, borderable, whiteBackground, active).link,
          ghost: getVariantColors(theme, clickable, borderable, whiteBackground, active).ghost,
          soft: getVariantColors(theme, clickable, borderable, whiteBackground, active).soft,
        },
        pill: {
          none: '',
          xs: 'ui-rounded-control-xs',
          sm: 'ui-rounded-control-sm',
          md: 'ui-rounded-control-md',
          lg: 'ui-rounded-control-lg',
          pill: 'ui-rounded-control-pill',
        },
        rounded: {
          false: '',
          true: 'ui-rounded-control-pill',
        },
        fullWidth: {
          true: 'ui-w-full',
          false: !square && !icon && children ? 'ui-min-w-fit' : '',
        },
      },
      defaultVariants: {
        size: 'md',
        color: 'primary',
        rounded: false,
        theme: 'solid',
      },
    }
  );
};

/**
 * Crea las variantes de color de texto usando CVA
 */
export const createTextColorVariants = () => {
  return cva('', {
    variants: {
      color: {
        primary: '',
        secondary: '',
        tertiary: '',
        destructive: '',
        link: '',
        ghost: '',
        soft: '',
      },
      textAlign: {
        left: 'ui-text-left',
        center: 'ui-text-center',
        right: 'ui-text-right',
      },
    },
    defaultVariants: {
      color: 'primary',
      textAlign: 'left',
    },
  });
};
